import { can } from "../../auth/can.ts";
import React, { useEffect } from "react";
import {
  getHasMotoPartner,
  getProceedingByPartnerAndType,
} from "../../services/proceedings.service.ts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import { FileType } from "../../interfaces/others/ILegacyDocument.ts";
import { notification } from "../../utils/Notifications.tsx";
import ExpedienteDocumentRow, {
  ExpedienteDocumentExpandedBody,
} from "./ExpedienteDocumentRow.tsx";
import LegacyDocItem from "./LegacyDocItem.tsx";

const DocumentItem = ({
  partnerId,
  file,
  documentsArray,
  proceedingsSummary,
  onEditClick,
  onLegacyEditClick,
  summaryLoading = false,
  legacyLoading = false,
  isLast = false,
  animationDelay = 0,
}) => {
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [fileData, setFileData] = React.useState<any[] | null>(null);
  const { user } = useSelector((state: RootState) => state.auth);
  const LEGACY_TYPES = React.useMemo(
    () => [
      "Foto colaborador",
      "INE frente y vuelta",
      "Comprobante de domicilio actual",
      "Original solicitud de empleo",
      "Pruebas psicométricas aplicadas por reclutamiento",
    ],
    []
  );
  const [legacyDoc, setLegacyDocuments] = React.useState<FileType | null>(
    documentsArray.find((doc) => doc.typeFile === file?.name) || null
  );
  const isLegacyType = LEGACY_TYPES.includes(file?.name);
  const [summary, setSummary] = React.useState<{
    count: number;
    lastUpdated: string | null;
  } | null>(null);

  const getDerivedStats = () => {
    const count = Array.isArray(fileData)
      ? fileData.length
      : summary?.count || 0;
    let lastUpdated: string | null = summary?.lastUpdated || null;
    if (Array.isArray(fileData) && fileData.length > 0) {
      const maxTs = fileData.reduce((acc, rec) => {
        const ts = new Date(rec.updateAt || rec.createdAt || 0).getTime();
        return Math.max(acc, ts);
      }, 0);
      if (maxTs > 0) lastUpdated = new Date(maxTs).toISOString();
    }
    return { count, lastUpdated };
  };

  const formatDate = (date: string) => {
    const newDate = new Date(date);
    return `${newDate.getDate()}/${
      newDate.getMonth() + 1
    }/${newDate.getFullYear()}`;
  };

  const getFileInformation = async (file: any) => {
    try {
      if (file.id === "motoDocument") {
        const hasMotoData = await getHasMotoPartner(partnerId);
        const updatedMotoFiles = hasMotoData.fileMotorcycleUploaded
          ? [...hasMotoData.fileDetails]
          : [];
        const motoDataList = updatedMotoFiles.map((motoFile: any) => ({
          id: file.id,
          fields: [],
          createdAt: new Date().toISOString(),
          updateAt: new Date().toISOString(),
          documentType: {
            id: file.id,
            name: file.name,
            fields: [],
          },
          file: motoFile,
          uploadBy: hasMotoData.uploadBy,
          url: motoFile.url,
        }));
        setFileData(motoDataList);
      } else {
        const data = await getProceedingByPartnerAndType(partnerId, file.id);
        setFileData(data);
      }
    } catch (error) {
      console.error(error);
      notification(
        "Error",
        "Ocurrió un error al recuperar los documentos",
        "danger"
      );
    }
  };

  useEffect(() => {
    if (summaryLoading && file.id !== "motoDocument") {
      setExpanded(false);
    }
  }, [summaryLoading, file.id]);

  useEffect(() => {
    if (!expanded) return;
    getFileInformation(file);
  }, [expanded, file]);

  useEffect(() => {
    const docId = String(file?.id);
    setSummary(proceedingsSummary?.[docId] || null);
  }, [proceedingsSummary, file?.id]);

  useEffect(() => {
    setLegacyDocuments(
      documentsArray.find((doc) => doc.typeFile === file?.name) || null
    );
  }, [file, documentsArray]);

  if (isLegacyType && !can("proceedings.review", user) && !can("partners.view_imss_salary", user)) return null;

  if (
    (legacyDoc?.typeFile ===
      "Pruebas psicométricas aplicadas por reclutamiento" ||
      legacyDoc?.typeFile ===
        "Alta en el IMSS (alta ingresada por contabilidad, no se visualiza este rubro)") &&
    !can("proceedings.review", user) && !can("partners.view_imss_salary", user)
  )
    return null;

  if (file?.name === "Notas del reclutador") return null;
  if (
    file?.name ===
    "Medio de transporte(si es motocicleta es de alto riesgo uso interno)"
  )
    return null;

  if (isLegacyType) {
    const docToUse = legacyDoc || {
      typeFile: file?.name || "",
      files: [],
      name: "",
    };

    return (
      <LegacyDocItem
        legacyDoc={docToUse}
        obligatory={file?.obligatory || false}
        isLoading={legacyLoading}
        isLast={isLast}
        animationDelay={animationDelay}
        onEditClick={() => {
          if (onLegacyEditClick) {
            onLegacyEditClick(docToUse);
          }
        }}
      />
    );
  }

  const motoFileCount =
    file.id === "motoDocument" ? file.files?.length || 0 : undefined;
  const displayCount =
    motoFileCount !== undefined ? motoFileCount : summary?.count || 0;
  const displayUpdated =
    file.id === "motoDocument" && file.files?.length
      ? null
      : summary?.lastUpdated || null;
  const hasFiles = displayCount > 0;
  const isMetaLoading = summaryLoading && file.id !== "motoDocument";

  const metaText =
    !isMetaLoading && (summary || motoFileCount !== undefined)
      ? `${displayCount} archivo${displayCount !== 1 ? "s" : ""}${
          displayUpdated ? ` · ${formatDate(displayUpdated)}` : ""
        }`
      : null;

  const stats = getDerivedStats();

  return (
    <ExpedienteDocumentRow
      title={file?.name}
      obligatory={Boolean(file?.obligatory)}
      metaText={metaText}
      isMetaLoading={isMetaLoading}
      hasFiles={hasFiles}
      expanded={expanded}
      onToggleExpand={() => setExpanded(!expanded)}
      isLast={isLast}
      animationDelay={animationDelay}
      expandedContent={
        <ExpedienteDocumentExpandedBody
          count={stats.count}
          lastUpdated={stats.lastUpdated}
          formattedDate={formatDate}
          onEdit={() => onEditClick?.(file, true)}
        />
      }
    />
  );
};

export default DocumentItem;
